import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { AccountLayout } from "../components/shell.tsx";
import { Button, EmptyState, ErrorState, PageHeader, Panel, Pill, Skeleton } from "../components/ui.tsx";
import { NewPlanDialog } from "./NewPlanDialog.tsx";
import { useSession } from "../lib/session.tsx";
import { get } from "../lib/api.ts";
import { roleLabel, type PlansResponse } from "../lib/types.ts";
import { formatDateBR, formatInstantBR } from "../../shared/dates.ts";
import { usePageTitle } from "../lib/nav.tsx";
import { useMaturityInstitutions } from "../lib/maturity.ts";
import { GuardedLink } from "../components/plan-shell.tsx";

export const PLANS_KEY = ["plans"] as const;

export function PlansPage() {
  usePageTitle("Minhas oficinas");
  const q = useQuery({ queryKey: PLANS_KEY, queryFn: () => get<PlansResponse>("/plans?page_size=100") });
  const { user } = useSession();
  const [creating, setCreating] = useState(false);
  const institutions = q.data?.meta.instructor_institutions ?? [];
  const maturity = useMaturityInstitutions();

  return (
    <AccountLayout>
      <PageHeader
        title="Minhas oficinas"
        subtitle={institutions.length ? "Escolha a oficina que deseja abrir ou crie uma nova." : "Escolha a oficina que deseja abrir."}
        actions={<>
          {(maturity.data?.length ?? 0) > 0 && (
            <GuardedLink href="/diagnosticos" className="inline-flex min-h-10 items-center rounded-ctl border border-line-strong bg-surface px-4 text-sm font-semibold text-ink shadow-e1 hover:bg-paper">
              Diagnóstico institucional
            </GuardedLink>
          )}
          {institutions.length > 0 && <Button onClick={() => setCreating(true)}>Nova oficina</Button>}
        </>}
      />
      {creating && <NewPlanDialog institutions={institutions} defaultFacilitator={user?.name ?? ""} onClose={() => setCreating(false)} />}
      {q.isPending ? (
        <Skeleton lines={4} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : q.data.data.length === 0 ? (
        <EmptyState title="Você ainda não participa de nenhuma oficina.">
          Quando o instrutor enviar um convite para o seu e-mail, a oficina aparecerá aqui.
        </EmptyState>
      ) : (
        <ul className="grid gap-4 md:grid-cols-2">
          {q.data.data.map((p) => {
            const expired = p.access_state === "expired";
            return (
              <li key={p.id}>
                <Panel as="article" className="lift flex h-full flex-col">
                  <div>{expired ? <Pill tone="gray">Acesso encerrado</Pill> : <Pill>Acesso disponível</Pill>}</div>
                  <h2 className="mt-2 text-lg font-bold">{p.name}</h2>
                  <p className="text-sm">
                    {p.institution.name} · {roleLabel[p.role]}
                  </p>
                  <p className="mt-1 text-xs text-muted">
                    Oficina: {formatDateBR(p.starts_on)} a {formatDateBR(p.ends_on)} · Participantes até {formatInstantBR(p.access_until)} (horário de Brasília)
                  </p>
                  <div className="mt-auto pt-4">
                    <Link
                      href={expired ? `/planos/${p.id}/acesso-encerrado` : `/planos/${p.id}/painel`}
                      className={
                        expired
                          ? "inline-flex rounded-ctl border border-field bg-white px-4 py-2 text-sm font-semibold text-ink"
                          : "inline-flex rounded-ctl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark"
                      }
                    >
                      {expired ? "Ver situação do acesso" : "Abrir oficina"}
                    </Link>
                  </div>
                </Panel>
              </li>
            );
          })}
        </ul>
      )}
    </AccountLayout>
  );
}
